import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress, ThemeProvider, Box } from '@material-ui/core';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { logError } from '@/utils/logError';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import sleepioTheme from '@/themes/sleepioTheme';

// Hardcoded fallbacks used when sessionStorage is empty (ephemeral ASWebAuthenticationSession
// opened by the Canopy foreground fallback resolver — no shared storage with Safari).
//
// Sign-up lands on authCallback because that screen owns the magic-link browser session
// and needs to be the ASWebAuthenticationSession intercept target.
// Login uses exchange-callback — a dedicated screen for token-only session handoff.
const CANOPY_SIGN_UP_CALLBACK = 'sleep-companion://authCallback';
const CANOPY_LOGIN_CALLBACK = 'sleep-companion://exchange-callback';

const EmailCallbackInner: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const base = useActivationBase();
  const fallbackCallback = state.accountIntent === AccountIntent.Signin
    ? CANOPY_LOGIN_CALLBACK
    : CANOPY_SIGN_UP_CALLBACK;
  const effectiveRedirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY) || fallbackCallback;
  const navigatePostAuth = usePostAuthNavigation(effectiveRedirectTo);

  useEffect(() => {
    const navigateToAuth = () => history.replace(`${base}/auth`);

    const handleCallback = async () => {
      await new Promise((resolve) => { setTimeout(resolve, 1500); });

      const { data: { session } } = await getSupabaseClient().auth.getSession();
      if (!session) {
        logError('[SleepioEmailCallback]', 'No session after magic link');
        navigateToAuth();
        return;
      }

      dispatch({ type: 'SET_SESSION', session });
      dispatch({ type: 'SET_REDIRECT_TO', redirectTo: effectiveRedirectTo });

      if (state.accountIntent === AccountIntent.Create) {
        await activateSleepioConfirm({ accessToken: session.access_token });
      }

      navigatePostAuth({ replace: true });
    };

    handleCallback().catch((err) => {
      logError('[SleepioEmailCallback]', 'Callback failed', err);
      navigateToAuth();
    });
  }, []); // eslint-disable-line

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
};

export const EmailCallback: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <EmailCallbackInner />
  </ThemeProvider>
);
